import memoize from 'memoizee';
import { ethers } from 'ethers';
import Web3 from 'web3';
import { IS_DEV } from '@/constants/AppConstants';
import config from '@/constants/config';
import { notifyHandler, notifyNotification } from '@/init';
import { contractAbis, MULTICALL2_ABI } from '@/constants/abis';
import factoryV2Abi from '@/constants/abis/json/factory-v2-registry';
import { sleep } from '@/utils/Async';
import { getArrayChunks, flattenArray } from '@/utils/Array';
import { toSignatureAwareMethodName } from '@/utils/Ethersjs';
import { gas as contractGas, contract as currentContract } from '@/contract';
import * as errorStore from '@/components/common/errorStore';
import * as gasPriceStore from '@/components/common/gasPriceStore';

const infuraWeb3 = new Web3(config.rpcUrl);
const web3 = () => currentContract?.web3 || infuraWeb3;

const getPendingWaitingMessage = (pendingWaitingMessage, txHash) => (
    pendingWaitingMessage.replace('[TX_URL]', `${config.explorerBaseUrl.tx}${txHash}`)
)

const callMethod = async ({
    contract, // e.g. currentContract
    contractName = 'swap',
    fromAddress, // e.g. contract.default_account
    methodName, // e.g. 'add_liquidity'
    params = [], // Array of params, if the method takes any
    extraData: {
        ethValue = undefined, // Amount of ETH transfered, if any
        ethereumContractGasEstimationFallback = undefined, // Either a number, or an object of shape { methodName, poolId } (with methodName being a method name on contractGas)
        bypassGasEstimationFailureWarning = undefined, // Used in conjunction w/ a warning modal, see OneSplit
    } = {},
    // Pass additional, optional parameters to update state around the tx
    vueState: {
        vueInstance = undefined, // Optional, pass if we want to modify state such as `estimateGas`, `waitingMessage`
        initialWaitingMessage = 'Please confirm transaction', // Message displayed before tx is confirmed
        pendingWaitingMessage = `Waiting for <a href="[TX_URL]">transaction</a> to confirm: no further action needed`, // Message displayed when tx is confirmed and pending; use [TX_URL] placeholder to link to the tx
    } = {},
    onSuccess = () => {},
    // ${config.explorerBaseUrl.tx}${sendTransactionPromise.hash}
}) => {
    if (typeof contract === 'undefined') {
        throw new Error('callMethod error: param `contract` required');
    }
    if (typeof fromAddress === 'undefined') {
        throw new Error('callMethod error: param `fromAddress` required');
    }
    if (typeof methodName === 'undefined') {
        throw new Error('callMethod error: param `methodName` required');
    }
    if (
        config.blockchainId === 'ethereum' &&
        typeof ethereumContractGasEstimationFallback !== 'number' &&
        typeof ethereumContractGasEstimationFallback !== 'object'
    ) {
        throw new Error('callMethod error: param `extraData.ethereumContractGasEstimationFallback` is required for Ethereum, and must be a number or an object');
    }
    if (
        config.blockchainId === 'ethereum' &&
        typeof ethereumContractGasEstimationFallback === 'object' &&
        (!ethereumContractGasEstimationFallback.methodName || !ethereumContractGasEstimationFallback.poolId)
    ) {
        throw new Error('callMethod error: if param `extraData.ethereumContractGasEstimationFallback` is an object, its shape must be { methodName, poolId }');
    }


    if (vueInstance) vueInstance.waitingMessage = initialWaitingMessage;
    const { dismiss } = notifyNotification(initialWaitingMessage)
    await sleep(100);

    const isFakePlainPool = !!contract.crypto_zap;
    const resolvedContractName = (
        contractName === 'gauge' ? 'gaugeContract' :
        contractName === 'oldGauge' ? 'oldGauge' :
        contractName === 'minter' ? 'minter' :
        (isFakePlainPool && contractName !== 'gaugeContract' && contractName !== 'migrator') ? 'crypto_zap' : // TODO check if this is a reliable condition
        contractName
    )

    let estimatedGas = 0;
    let success = true;
    if (config.blocknativeSupport === true) {
        try {
            estimatedGas = await contract[resolvedContractName].methods[methodName](...params).estimateGas({
                from: fromAddress,
                //gas: 1800000, // in theory no need to specify a value as if value ends up being too low then there can be a revert here
                ...(ethValue ? { value: ethValue } : {}),
            });
            vueInstance.estimateGas = estimatedGas;
            estimatedGas = (estimatedGas * 1.4).toFixed(0);
            vueInstance.errorEstimating = null;
            vueInstance.showFailedGasEstimationModal = false;
        } catch (e) {
            if (ethereumContractGasEstimationFallback?.methodName) {
                estimatedGas = contractGas[ethereumContractGasEstimationFallback.methodName][ethereumContractGasEstimationFallback.poolId];
                vueInstance.estimateGas = estimatedGas;
            } else if (typeof ethereumContractGasEstimationFallback === 'number') {
                estimatedGas = ethereumContractGasEstimationFallback; // Number
                vueInstance.estimateGas = estimatedGas;
            }

            vueInstance.errorEstimating = true;
            vueInstance.showFailedGasEstimationModal = true;
        }

        // If there's a gas estimation failure warning to display, and user hasn't already
        // bypassed it by clicking "proceed nonetheless", abort trade and let the modal show.
        if (vueInstance.showFailedGasEstimationModal && bypassGasEstimationFailureWarning === false) {
            dismiss();
            vueInstance.waitingMessage = '';
            vueInstance.show_loading = false;
            return;
        }
        vueInstance.showFailedGasEstimationModal = false;

        const sendTx = async () => (
            (await sendSafe(contract[resolvedContractName].methods[methodName](...params), {
                from: fromAddress,
                gas: estimatedGas,
                ...(ethValue ? { value: ethValue } : {}),
            })).pe.once('transactionHash', (hash) => {
                dismiss();
                notifyHandler(hash);
                vueInstance.waitingMessage = getPendingWaitingMessage(pendingWaitingMessage, hash);
            })
        );

        let receipt;

        try {
            receipt = await sendTx();
        } catch(err) {
            console.error(err);
            dismiss();
            errorStore.handleError(err);
            success = false;

            if(err.code == -32603) {
                await sleep(300)
                receipt = await sendTx();
                success = true;
            }
        }

        dismiss();
        vueInstance.waitingMessage = '';
        vueInstance.show_loading = false;

        if (success) onSuccess();
        return receipt;
    } else {
        console.log('callMethod', { contract, contractName, methodName, params, fromAddress, ethValue });

        estimatedGas = await contract[resolvedContractName].methods[methodName](...params).estimateGas({
            from: fromAddress,
            ...(ethValue ? { value: ethValue } : {}),
        });

        console.log('estimatedGas', estimatedGas, 'method', methodName)
        if (vueInstance) {
            vueInstance.estimateGas = estimatedGas / 2;
        }

        if (config.blockchainId === 'polygon') {
          estimatedGas = estimatedGas * 1.5
        }
        if (config.blockchainId === 'xdai' && estimatedGas > 10000000) { //
          //some weird bug where estimateGas on xdai returns some very high number so we change to a smaller number
          //can be reproduced on gauge deposit
          estimatedGas = 1000000
        }

        const signer = (
            contract.signer ||
            (contract.provider || new ethers.providers.Web3Provider(window.ethereum)).getSigner()
        );

        let contractAddress;
        let contractAbi;

        if (contractName === 'gauge') {
            contractAddress = contract.gaugeContract._address;
            contractAbi = contract.gaugeContract._jsonInterface;
        } else if (contractName === 'oldGauge') {
            contractAddress = contract.oldGauge._address;
            contractAbi = contract.oldGauge._jsonInterface;
        } else if (contractName === 'minter') {
            contractAddress = contract.minter._address;
            contractAbi = contract.minter._jsonInterface;
        } else if (contractName === 'migrator') {
            contractAddress = contract.migrator._address;
            contractAbi = contract.migrator._jsonInterface;
        } else if (contractName === 'factory') {
            contractAddress = config.factoryV2RegistryAddress;
            contractAbi = factoryV2Abi;
        } else if (isFakePlainPool) {
            contractAddress = contract.crypto_zap._address;
            contractAbi = contract.crypto_zap._jsonInterface;
        } else {
            contractAddress =
                contractAbis[contract.currentContract][contractName === 'swap' ? 'swap_address' : 'deposit_address'];
            contractAbi =
                contractAbis[contract.currentContract][contractName === 'swap' ? 'swap_abi' : 'deposit_abi'];
        }

        const ethersContract = new ethers.Contract(
            contractAddress,
            contractAbi,
            signer
        );

        const ethersMethodName = toSignatureAwareMethodName({
            methodName,
            abi: contractAbi,
            params,
        });


        console.log(params, ethValue, 'params, ethValue')
        const sendTransactionPromise = await ethersContract[ethersMethodName](...params, {
            gasLimit: estimatedGas * 1.3 | 0,
            ...gasPriceStore.getGasFields(),
            ...(ethValue ? { value: ethValue } : {}),
        });

        dismiss();
        notifyHandler(sendTransactionPromise.hash); // TODO prob not needed in chain w/o bnc support
        if (vueInstance) {
            vueInstance.waitingMessage = getPendingWaitingMessage(pendingWaitingMessage, sendTransactionPromise.hash);
            const receipt = await sendTransactionPromise.wait();
            vueInstance.waitingMessage = '';
            vueInstance.show_loading = false;

            onSuccess();
            return receipt;
        }
    }
}

// Contract instances cache store
const getContractInstance = memoize((address, abi, library) => (
    new library.eth.Contract(abi, address)
));

const FALLBACK_DECODED_PARAMETERS_VALUES = {
    uint256: '0',
    address: '0x0000000000000000000000000000000000000000',
};

/**
 * @param {Array<{contract: Object, methodName: String, params: Array}>} callsConfig
 *
 * Returns an array of data.
 * If `metaData` is passed alongside any call, returns an array of objects of shape { data, metaData } instead.
 */
const multiCall = async (callsConfig, isDebugging = false) => {
    const defaultCallConfig = {
        // Pass either a contract object (if that contract object is already instantiated and it's easier)
        contract: undefined, // e.g. currentContract
        // Or pass both address and abi for the utility to instantiate and cache the contract instance
        address: undefined,
        abi: undefined,
        methodName: undefined, // e.g. 'claimable_tokens'
        params: [], // Array of params, if the method takes any
        // Optional; any data to be passed alongside each call's results, for example to act as a marker
        // to easily identify what the call's results reference
        metaData: undefined,
    };

    const augmentedCallsConfig = callsConfig.map((config) => ({
        ...defaultCallConfig,
        ...config,
    }));

    // Validate configs
    for (const config of augmentedCallsConfig) {
        const usesContractField = typeof config.contract !== 'undefined';

        if (usesContractField && typeof config.contract !== 'object') {
            throw new Error('multiCall error: config parameter `contract` expects a contract object');
        }

        if (!usesContractField && typeof config.address !== 'string') {
            console.log(config)
            throw new Error('multiCall error: config parameter `address` expects a contract address');
        }

        if (!usesContractField && !Array.isArray(config.abi)) {
            throw new Error('multiCall error: config parameter `abi` expects an array');
        }

        if (typeof config.methodName !== 'string') {
            throw new Error('multiCall error: config parameter `methodName` expects a contract method name');
        }

        if (usesContractField && !config.contract._address) {
            console.error({ config });
            throw new Error('multiCall error: couldn’t find any `_address` property on config parameter `contract`; either the contract object passed in incorrect, or we need to make multiCall accept an optional address param to pass it manually ourselves when it’s not implicitly set on `contract`');
        }
    }

    const hasMetaData = augmentedCallsConfig.some(({ metaData }) => typeof metaData !== 'undefined');

    const calls = augmentedCallsConfig.map(({
        contract,
        address,
        abi,
        methodName,
        params,
    }) => [
        contract?._address || address,
        (contract || getContractInstance(address, abi, web3())).methods[methodName](...params).encodeABI(),
    ]);

    const multicall = getContractInstance(config.multicall2Address, MULTICALL2_ABI, web3());
    const chunkedReturnData = [];
    const chunkedCalls = getArrayChunks(calls, 200); // Keep each multicall size reasonable

    let decodedData;
    try {
        for (const callsChunk of chunkedCalls) {
            const aggregateReturnData = await multicall.methods.tryAggregate(false, callsChunk).call();
            const returnData = aggregateReturnData.map(({ returnData }) => returnData);
            chunkedReturnData.push(returnData);
        }

        const returnData = flattenArray(chunkedReturnData);

        decodedData = returnData.map((hexData, i) => {
            const { contract, abi, methodName, metaData } = augmentedCallsConfig[i];
            const contractAbi = contract?._jsonInterface || abi;
            const outputSignature = contractAbi.find(({ name }) => name === methodName).outputs;

            let data;
            if (outputSignature.length > 1) {
                try {
                    data = web3().eth.abi.decodeParameters(outputSignature.map(({ type, name }) => ({ type, name })), hexData);
                } catch (err) {
                    console.error(`Failed decodeParameters with outputSignature ${JSON.stringify(outputSignature.map(({ type, name }) => ({ type, name })))}`);

                    throw err;
                }
            } else {
                try {
                    data = web3().eth.abi.decodeParameter(outputSignature[0].type, hexData);
                } catch (err) {
                    const failedDecodedType = outputSignature[0].type;

                    // Use fallback value if one exists (ideally we have fallback values for all types,
                    // add more when necessary as we encounter other failures)
                    if (typeof FALLBACK_DECODED_PARAMETERS_VALUES[failedDecodedType] !== 'undefined') {
                        data = FALLBACK_DECODED_PARAMETERS_VALUES[failedDecodedType];
                    } else {
                        console.error(`Failed decodeParameter with outputSignature ${JSON.stringify(failedDecodedType)}`);
                        throw err;
                    }
                }
            }

            if (hasMetaData) return { data, metaData };
            return data;
        });
    } catch (err) {
        console.error(err)
        if (IS_DEV && !isDebugging) await findThrowingCall(callsConfig);
        else throw err;
    }

    return decodedData;
};

const findThrowingCall = async (callsConfig) => {
  console.warn('multiCall() threw, running debugger...');
  let subset = callsConfig;

  while (subset.length > 1) {
    const midIndex = Math.ceil(subset.length / 2);
    const slices = [subset.slice(0, midIndex), subset.slice(-midIndex)];

    // eslint-disable-next-line no-restricted-syntax
    for (const slice of slices) {
      try {
        await multiCall(slice, true); // eslint-disable-line no-await-in-loop
      } catch (err) {
        subset = slice;
      }
    }
  }

  console.log('Found throwing call:', subset);
};

// Validates an Ethereum address, and resolves ENS names.
// Returns either a valid Ethereum address, or null if the input was invalid.
const getNormalizedEthereumAddress = memoize(async (input) => {
    const isValidAddress = web3().utils.isAddress(input);
    if (isValidAddress) return input;

    // We can fail early if there's no dot in this string, it's a simple heuristic for invalid names
    if (!input.includes('.')) return null;

    try {
        const address = await web3().eth.ens.getAddress(input);
        return address; // Will throw if name wasn't valid
    } catch (err) {}

    return null;
}, {
    promise: true,
    maxAge: 30 * 60 * 1000, // 30 min
});

/**
 * Wrapper for web3js method#send that passes eip 1559 fields, and falls back to
 * pre-eip1559 fields when the provider isn't compatible with eip1559. Doesn't use
 * eip1559 fields for chains other than Ethereum.
 *
 * Returns the original promiseEvent object on success for chainability, otherwise
 * returns a second promiseEvent object, obtained from calling the same method but
 * with pre-eip1559 fields.
 *
 * Usage: `(await sendSafe(contract.method(params), { from, value, gas })).pe.once('transactionHash', cb)`
 *
 * Note: the { pe } wrapper is necessary because some Promise implementations don't work
 * well with resolving a promise (a single await will await both the original promise, and
 * the returned one, which is not what we want), so we wrap the resolved promise in an
 * object to prevent confusing the promise implementation.
 */
const sendSafe = (method, sendParams) => new Promise((resolve) => {
    console.log({ method, sendParams })
    const promiseEvent = method.send({
        ...sendParams,
        ...gasPriceStore.getGasFields(),
    })
        .on('transactionHash', () => {
            resolve({ pe: promiseEvent })
        })
        .on('error', (err) => {
            if (err.message.includes('EIP-1559')) {
                const otherPE = method.send({
                    ...sendParams,
                    ...gasPriceStore.getGasFields({ forcePreEip1559: true }),
                });
                resolve({ pe: otherPE });
            } else {
                resolve({ pe: promiseEvent });
            }
        });
});

export {
    callMethod,
    multiCall,
    getNormalizedEthereumAddress,
    sendSafe,
    web3,
};
